import * as React from "react";

import { useIsMobile } from "../../hooks/useIsMobile";
import { FieldDescription } from "../input/field";
import { Label } from "../Label";
import { Popover } from "../Popover";
import { SelectContext } from "./primitives/context";
import type { SelectRootProps } from "./types";

type EnhancedSelectBaseProps = Omit<
  SelectRootProps,
  "value" | "defaultValue" | "onValueChange"
> & {
  searchable?: boolean;
  creatable?: boolean;
  placeholder?: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
};

type EnhancedSelectProps =
  | (EnhancedSelectBaseProps & {
      multiple?: false;
      value?: string;
      defaultValue?: string;
      onValueChange?: (v: string) => void;
    })
  | (EnhancedSelectBaseProps & {
      multiple: true;
      value?: string[];
      defaultValue?: string[];
      onValueChange?: (v: string[]) => void;
    });

const getSelectedValues = ({
  isMulti,
  value,
  isValueControlled,
  uncontrolledMultiValue,
  uncontrolledSingleValue,
}: {
  isMulti: boolean;
  value: string | string[] | undefined;
  isValueControlled: boolean;
  uncontrolledMultiValue: string[];
  uncontrolledSingleValue: string;
}) => {
  if (isMulti) {
    if (Array.isArray(value)) return value;
    return isValueControlled ? [] : uncontrolledMultiValue;
  }

  if (typeof value === "string") return value ? [value] : [];
  if (isValueControlled) return [];
  return uncontrolledSingleValue ? [uncontrolledSingleValue] : [];
};

// This keeps the enhanced select working with HTML forms and native validation
const NativeSelectField = ({
  name,
  required,
  disabled,
  isMulti,
  selectedValues,
}: {
  name: string;
  required?: boolean;
  disabled?: boolean;
  isMulti: boolean;
  selectedValues: string[];
}) => {
  if (isMulti) {
    return (
      <select
        multiple
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        required={required}
        disabled={disabled}
        value={selectedValues}
        onChange={() => {}}
        className="sr-only"
      >
        <option value="" />
        {selectedValues.map((selectedValue) => (
          <option key={selectedValue} value={selectedValue}>
            {selectedValue}
          </option>
        ))}
      </select>
    );
  }

  const selectedValue = selectedValues[0] ?? "";

  return (
    <select
      tabIndex={-1}
      aria-hidden="true"
      name={name}
      required={required}
      disabled={disabled}
      value={selectedValue}
      onChange={() => {}}
      className="sr-only"
    >
      <option value="" />
      {selectedValue ? (
        <option value={selectedValue}>{selectedValue}</option>
      ) : null}
    </select>
  );
};

export const EnhancedSelectRoot = ({
  searchable: searchableProp = false,
  creatable = false,
  multiple,
  placeholder,
  label,
  description,
  value,
  defaultValue,
  onOpenChange,
  open: controlledOpen,
  defaultOpen,
  disabled,
  onValueChange,
  required,
  name,
  dir,
  children,
  ...rest
}: EnhancedSelectProps) => {
  const isMobile = useIsMobile();
  const searchable = creatable ? true : searchableProp;
  const isMulti = creatable ? true : multiple === true;

  const isOpenControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const actualOpen = isOpenControlled ? controlledOpen : uncontrolledOpen;

  const isValueControlled = value !== undefined;
  const [uncontrolledSingleValue, setUncontrolledSingleValue] = React.useState(
    typeof defaultValue === "string" ? defaultValue : "",
  );
  const [uncontrolledMultiValue, setUncontrolledMultiValue] = React.useState<
    string[]
  >(
    Array.isArray(defaultValue)
      ? defaultValue
      : typeof defaultValue === "string" && defaultValue
        ? [defaultValue]
        : [],
  );
  const [search, setSearch] = React.useState("");
  const [labelsVersion, setLabelsVersion] = React.useState(0);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!isOpenControlled) setUncontrolledOpen(open);
      if (!open) setSearch("");
      onOpenChange?.(open);
    },
    [isOpenControlled, onOpenChange],
  );

  const selectedValues = React.useMemo(
    () =>
      getSelectedValues({
        isMulti,
        value,
        isValueControlled,
        uncontrolledMultiValue,
        uncontrolledSingleValue,
      }),
    [
      isMulti,
      value,
      isValueControlled,
      uncontrolledMultiValue,
      uncontrolledSingleValue,
    ],
  );

  const selectedValueSet = React.useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const updateValue = React.useCallback(
    (nextValues: string[]) => {
      if (isMulti) {
        if (!isValueControlled) setUncontrolledMultiValue(nextValues);
        (onValueChange as ((v: string[]) => void) | undefined)?.(nextValues);
        return;
      }
      const nextValue = nextValues[0] ?? "";
      if (!isValueControlled) setUncontrolledSingleValue(nextValue);
      (onValueChange as ((v: string) => void) | undefined)?.(nextValue);
    },
    [isMulti, isValueControlled, onValueChange],
  );

  const itemLabelsByValue = React.useRef<Map<string, string>>(new Map());
  const registerItem = React.useCallback((v: string, lbl: string) => {
    if (itemLabelsByValue.current.get(v) === lbl) {
      return;
    }
    itemLabelsByValue.current.set(v, lbl);
    setLabelsVersion((current) => current + 1);
  }, []);

  const handleItemSelect = React.useCallback(
    (optionValue: string, itemLabel?: string) => {
      if (
        itemLabel !== undefined &&
        itemLabelsByValue.current.get(optionValue) !== itemLabel
      ) {
        itemLabelsByValue.current.set(optionValue, itemLabel);
        setLabelsVersion((current) => current + 1);
      }

      if (isMulti) {
        const has = selectedValues.includes(optionValue);
        updateValue(
          has
            ? selectedValues.filter(
                (currentValue) => currentValue !== optionValue,
              )
            : [...selectedValues, optionValue],
        );
        return;
      }

      updateValue([optionValue]);
      handleOpenChange(false);
    },
    [handleOpenChange, isMulti, selectedValues, updateValue],
  );

  const removeValue = React.useCallback(
    (optionValue: string) => {
      // Remove created-only values from the label map so "Create X" shows again when retyped.
      if (itemLabelsByValue.current.get(optionValue) === optionValue) {
        itemLabelsByValue.current.delete(optionValue);
      }
      updateValue(
        selectedValues.filter((currentValue) => currentValue !== optionValue),
      );
    },
    [selectedValues, updateValue],
  );

  const selectContextValue = React.useMemo(() => {
    void labelsVersion;

    return {
      isSimple: false,
      isMobile,
      open: actualOpen,
      handleOpenChange,
      selectedValues,
      selectedValueSet,
      handleItemSelect,
      removeValue,
      search,
      setSearch,
      searchable,
      creatable,
      multiple: isMulti,
      placeholder,
      label,
      disabled,
      registerItem,
      itemLabelsByValue,
    };
  }, [
    isMobile,
    actualOpen,
    handleOpenChange,
    selectedValues,
    selectedValueSet,
    handleItemSelect,
    removeValue,
    search,
    searchable,
    creatable,
    isMulti,
    placeholder,
    label,
    disabled,
    registerItem,
    labelsVersion,
  ]);

  void rest;

  return (
    <>
      {label ? <Label>{label}</Label> : null}
      <div className="relative w-full text-sm text-black">
        <SelectContext value={selectContextValue}>
          <div dir={dir} className="w-full">
            {isMobile ? (
              children
            ) : (
              <Popover open={actualOpen} onOpenChange={handleOpenChange}>
                {children}
              </Popover>
            )}
            {name ? (
              <NativeSelectField
                name={name}
                required={required}
                disabled={disabled}
                isMulti={isMulti}
                selectedValues={selectedValues}
              />
            ) : null}
          </div>
        </SelectContext>
      </div>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
    </>
  );
};
